import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fetchPromotionCoveragePreview } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchPromotionCoveragePreview";
import type { PromotionCoveragePreview } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchPromotionCoveragePreview";

interface ConfirmRegisterWaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  userFullName: string;
  enrollmentId: number | null;
  onConfirm: (
    sendNotification: boolean,
    voucherId: number | null,
  ) => Promise<void>;
}

function formatMoney(amount: string): string {
  const parsed = Number(amount);
  if (Number.isNaN(parsed)) return amount;
  return parsed.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function ConfirmRegisterWaitlistModal({
  isOpen,
  onClose,
  userFullName,
  enrollmentId,
  onConfirm,
}: ConfirmRegisterWaitlistModalProps) {
  const [isWorking, setIsWorking] = useState(false);
  const [sendNotification, setSendNotification] = useState(true);
  const [preview, setPreview] = useState<PromotionCoveragePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [useVoucher, setUseVoucher] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen || enrollmentId === null) {
      setPreview(null);
      setUseVoucher(false);
      setSelectedVoucherId(null);
      return;
    }
    setPreviewLoading(true);
    fetchPromotionCoveragePreview({ enrollmentId })
      .then((response) => {
        setPreview(response.data);
        // Preselect the earliest-expiring voucher (same FIFO drain
        // order checkout uses), but leave the opt-in unchecked — the
        // admin decides whether to spend a voucher on this seat.
        setSelectedVoucherId(response.data.vouchers[0]?.id ?? null);
      })
      .finally(() => setPreviewLoading(false));
  }, [isOpen, enrollmentId]);

  const isCash = preview !== null && preview.coverage === "cash";
  const hasVoucherOptions =
    isCash && preview.isVoucherEligible && preview.vouchers.length > 0;

  const handleConfirm = async () => {
    setIsWorking(true);
    try {
      await onConfirm(
        sendNotification,
        useVoucher && isCash ? selectedVoucherId : null,
      );
      onClose();
    } catch (error) {
      console.error("Failed to register from waitlist:", error);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Register from Waitlist</DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-400">
            Are you sure you want to register <strong>{userFullName}</strong>{" "}
            from the waitlist?
          </DialogDescription>
        </DialogHeader>

        {previewLoading && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Checking billing coverage…
          </p>
        )}

        {preview !== null && preview.coverage === "total_access" && (
          <div className="rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
            This seat will be <strong>$0.00</strong> — covered by the
            company&apos;s Total Access subscription.
          </div>
        )}

        {preview !== null && preview.coverage === "stage" && (
          <div className="rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
            This seat will be <strong>$0.00</strong> — covered by the
            company&apos;s Stage Growth membership.
          </div>
        )}

        {isCash && (
          <div className="space-y-3">
            <div className="rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
              This seat will be{" "}
              <strong>{formatMoney(preview.seatPrice)}</strong> (cash). You can
              send a payment link from the Billing tab after registering.
            </div>

            {hasVoucherOptions && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    checked={useVoucher}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    onChange={(e) => setUseVoucher(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Cover this seat with a voucher
                  </span>
                </label>

                {useVoucher && (
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-slate-100"
                    onChange={(e) =>
                      setSelectedVoucherId(Number(e.target.value))
                    }
                    value={selectedVoucherId ?? ""}
                  >
                    {preview.vouchers.map((voucher) => (
                      <option key={voucher.id} value={voucher.id}>
                        {voucher.name} — {voucher.remainingSeats}{" "}
                        {voucher.remainingSeats === 1 ? "seat" : "seats"} left
                        {voucher.endDate ? `, expires ${voucher.endDate}` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            checked={sendNotification}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            onChange={(e) => setSendNotification(e.target.checked)}
            type="checkbox"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Send notification to the user
          </span>
        </label>

        <DialogFooter>
          <Button disabled={isWorking} onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isWorking || previewLoading}
            onClick={handleConfirm}
          >
            {isWorking ? "Registering..." : "Register"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmRegisterWaitlistModal;
